/**
 * TKP-203 — envanter özelliklerini aynı süreçteki başka bir veriden (veri kartı) ya da ilgili kişiden (ilgili kişi kartı) kopyalama.
 * Kip Ekle (kullanıcı kararı 2026-10-12): hedefin mevcut kayıtları silinmez, hedefte (ya da hedef satırın verisinde) aynı kalem varsa
 * yazılmaz, imha süresi yalnız hedefte boşsa yazılır. Adımlar panelin `runSteps` biçiminde ({ action, store, payload }) — her adım
 * mevcut tekil uçtan gider; onay açıkken taslak kapısı kendiliğinden işler. Saf: store/Vue yok.
 */
import { processingKey } from "./hierarchy-kip";

const sid = (x) => String(x && x._id ? x._id : x || "");
const norm = (v) => String(v || "").trim().toLocaleLowerCase("tr");
const nameKey = (name) => (name && typeof name === "object" ? norm(name.tr || name.en || Object.values(name).find(Boolean)) : norm(name));

/** Kaynak türü özellikler (toplama, saklama, arşiv) — panel sırasıyla. */
export const BAG_KEYS = ["directCollectMethods", "otherCollectMethods", "physicalStorages", "physicalArchives", "electronicStorages", "electronicArchives"];
const STORAGE_KEYS = ["physicalStorages", "electronicStorages"];
const BAG_STORES = {
  directCollectMethods: "datasourceDirectCollectMethod",
  otherCollectMethods: "datasourceOtherCollectMethod",
  physicalStorages: "datasourcePhysicalStorage",
  physicalArchives: "datasourcePhysicalArchive",
  electronicStorages: "datasourceElectronicStorage",
  electronicArchives: "datasourceElectronicArchive",
};

/** Bölümler panel sırasıyla; `row` = yalnız ilgili kişi satırında bulunan bölüm. */
export const COPY_SECTIONS = [
  { key: "processingLegals", label: "processing.text", row: true },
  { key: "directCollectMethods", label: "direct-collect-method.text" },
  { key: "otherCollectMethods", label: "other-collect-method.text" },
  { key: "physicalStorages", label: "physical-storage.text" },
  { key: "physicalArchives", label: "physical-archive.text" },
  { key: "electronicStorages", label: "electronic-storage.text" },
  { key: "electronicArchives", label: "electronic-archive.text" },
  { key: "transferings", label: "transfering.text", row: true },
  { key: "disposalPeriod", label: "disposal-method.periodic" },
  { key: "disposalMethods", label: "disposal-method.text" },
  { key: "measures", label: "inventory.node-form.measures-title" },
  { key: "accessAndRestrictions", label: "accessing-persons.text" },
  { key: "datasubjectRights", label: "datasubject-right.text", row: true },
  { key: "feature", label: "process-data.inventory-panel.processing-features", row: true },
];

/** Kütüphane kalemli kayıt anahtarı (kalem kimliği yoksa ad). */
const itemKey = (item) => (item && item.libraryItem ? `l:${sid(item.libraryItem)}` : `n:${nameKey(item && item.name)}`);
const storageKey = (item) => (item && (item.libraryItem || item.name) ? itemKey(item) : `bos:${norm(item && item.description)}|${(item && item.period) || ""}`);
const accessKey = (item) => (item && item.libraryItem ? `l:${sid(item.libraryItem)}` : item && item.accessor && item.accessor.name ? `n:${nameKey(item.accessor.name)}` : `k:${sid(item && item.restriction)}`);
const keyOf = (key) => (STORAGE_KEYS.includes(key) ? storageKey : key === "accessAndRestrictions" ? accessKey : itemKey);

const uniqueBy = (list, key) => {
  const seen = new Set();
  return (list || []).filter((x) => {
    if (!x || x.__draft) return false;
    const k = key(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/** Verinin etkin özellikleri: kendi kayıtları + kaynak seviyesindekiler (panelin gösterdiği birleşim). */
export const nodeSnapshot = (node = {}, inv = {}) => {
  const bags = {};
  for (const key of BAG_KEYS) {
    const own = ((inv.own || {})[key] || []);
    const bySource = Object.values(inv.datasources || {}).flatMap((bag) => (bag || {})[key] || []);
    bags[key] = uniqueBy([...own, ...bySource], keyOf(key));
  }
  return {
    bags,
    disposalMethods: uniqueBy(inv.disposalMethods, itemKey),
    accessAndRestrictions: uniqueBy(inv.accessAndRestrictions, accessKey),
    disposalPeriod: Number(inv.disposalPeriod != null ? inv.disposalPeriod : (node.feature || {}).disposalPeriod) || 0,
    administrativeMeasures: node.administrativeMeasures || [],
    technicalMeasures: node.technicalMeasures || [],
  };
};

/** İlgili kişi satırının kendi özellikleri. */
export const rowSnapshot = (row = {}, inv = {}) => {
  const bags = {};
  for (const key of BAG_KEYS) bags[key] = uniqueBy(((inv.own || {})[key] || []), keyOf(key));
  return {
    bags,
    disposalMethods: uniqueBy(inv.disposalMethods, itemKey),
    accessAndRestrictions: uniqueBy(inv.accessAndRestrictions, accessKey),
    disposalPeriod: Number(inv.disposalPeriod) || 0,
    administrativeMeasures: inv.administrativeMeasures || [],
    technicalMeasures: inv.technicalMeasures || [],
    processingLegals: uniqueBy(inv.processingLegals, processingKey),
    transferings: uniqueBy(inv.transferings, (t) => sid(t.thirdParty)),
    datasubjectRights: uniqueBy(inv.datasubjectRights, itemKey),
    feature: { autoProcessing: !!((row.feature || {}).autoProcessing), aiProcessing: !!((row.feature || {}).aiProcessing) },
  };
};

/** Bölümün kalem sayısı (pencerede gösterilir; 0 → bölüm listelenmez). */
export const sectionCount = (snap = {}, key) => {
  if (BAG_KEYS.includes(key)) return ((snap.bags || {})[key] || []).length;
  if (key === "disposalPeriod") return snap.disposalPeriod > 0 ? 1 : 0;
  if (key === "measures") return (snap.administrativeMeasures || []).length + (snap.technicalMeasures || []).length;
  if (key === "feature") return snap.feature ? Number(!!snap.feature.autoProcessing) + Number(!!snap.feature.aiProcessing) : 0;
  return (snap[key] || []).length;
};

/** Pencerenin bölüm listesi: kaynakta dolu olanlar (veri kopyasında satır bölümleri eşleşen satırların toplamıyla). */
export const copySections = (sources = []) =>
  COPY_SECTIONS.map((s) => ({ ...s, count: (sources || []).reduce((n, snap) => n + sectionCount(snap, s.key), 0) })).filter((s) => s.count > 0);

const ref = (x) => (x && x._id ? { _id: String(x._id) } : null);
const libRef = (item, name) => ({ _id: sid(item.libraryItem), name: name || item.name });
const level = (target) => (target.rowId ? { datasubject: { _id: target.rowId } } : { processData: { _id: target.nodeId } });
const has = (list, key, item) => (list || []).some((x) => x && key(x) === key(item));

/**
 * Tek seviyenin yazım adımları. `target`: { nodeId } ya da { rowId, nodeId }; `existing`: hedefin anlık görüntüsü;
 * `inherited`: satır hedefinde verisinin etkin özellikleri (orada olan kalem satıra ayrıca yazılmaz).
 */
export const levelSteps = ({ source = {}, existing = {}, inherited = {}, target, sections = [] }) => {
  const on = new Set(sections || []);
  const steps = [];
  const skipped = [];
  const holds = (key, list, item, keyFn) => has(list, keyFn, item) || has(key in (inherited.bags || {}) ? inherited.bags[key] : inherited[key], keyFn, item);

  for (const key of BAG_KEYS) {
    if (!on.has(key)) continue;
    const keyFn = keyOf(key);
    for (const item of (source.bags || {})[key] || []) {
      if (holds(key, (existing.bags || {})[key], item, keyFn)) continue;
      if (STORAGE_KEYS.includes(key)) {
        const { _id, name, libraryItem, ...retention } = item;
        steps.push({ action: "create", store: `${BAG_STORES[key]}/save`, payload: { ...retention, ...(libraryItem ? { libraryItem: libRef(item) } : {}), ...level(target) } });
      } else if (item.libraryItem) {
        steps.push({ action: "create", store: `${BAG_STORES[key]}/addFromLibrary`, payload: { ...level(target), libraryItem: libRef(item) } });
      } else skipped.push({ key, item });
    }
  }

  if (on.has("disposalMethods"))
    for (const item of source.disposalMethods || []) {
      if (holds("disposalMethods", existing.disposalMethods, item, itemKey)) continue;
      if (item.libraryItem) steps.push({ action: "create", store: "dataDisposalMethod/addFromLibrary", payload: { ...level(target), libraryItem: libRef(item) } });
      else skipped.push({ key: "disposalMethods", item });
    }

  if (on.has("accessAndRestrictions"))
    for (const item of source.accessAndRestrictions || []) {
      if (holds("accessAndRestrictions", existing.accessAndRestrictions, item, accessKey)) continue;
      const payload = { ...level(target), ...(item.restriction && item.restriction._id ? { restriction: item.restriction } : {}) };
      if (item.libraryItem) payload.libraryItem = libRef(item, item.accessor && item.accessor.name);
      steps.push({ action: "create", store: "dataAccessAndRestriction/save", payload });
    }

  // imha süresi: yalnız hedefte boşsa (satırda boş süre verininkini kullanır — satıra yalnız kaynak satırın kendi süresi)
  if (on.has("disposalPeriod") && source.disposalPeriod > 0 && !(existing.disposalPeriod > 0)) {
    steps.push(target.rowId
      ? { action: "period", store: "organizationProcessDataDatasubject/updateFeature", payload: { _id: target.rowId, disposalPeriod: source.disposalPeriod } }
      : { action: "period", store: "organizationProcessData/updateDisposalPeriod", payload: { _id: target.nodeId, disposalPeriod: source.disposalPeriod } });
  }

  if (on.has("measures")) {
    const merge = (cur, add) => {
      const list = (cur || []).map(ref).filter(Boolean);
      for (const x of (add || []).map(ref).filter(Boolean)) if (!list.some((y) => y._id === x._id)) list.push(x);
      return list;
    };
    const administrativeMeasures = merge(existing.administrativeMeasures, source.administrativeMeasures);
    const technicalMeasures = merge(existing.technicalMeasures, source.technicalMeasures);
    if (administrativeMeasures.length > (existing.administrativeMeasures || []).length || technicalMeasures.length > (existing.technicalMeasures || []).length) {
      const payload = { _id: target.rowId || target.nodeId, administrativeMeasures, technicalMeasures };
      steps.push({ action: "update", store: target.rowId ? "organizationProcessDataDatasubject/updateFeature" : "organizationProcessData/update", payload });
    }
  }

  if (target.rowId) {
    if (on.has("processingLegals")) {
      const items = (source.processingLegals || []).filter((x) => !has(existing.processingLegals, processingKey, x));
      if (items.length) {
        const rowRef = { _id: target.rowId };
        steps.push({ action: "create", store: "datasubjectProcessingLegal/create", payload: { datasubject: rowRef, items: items.map((x) => ({ purpose: x.purpose, legalReason: x.legalReason, legitimateInterest: x.legitimateInterest, datasubject: rowRef })) } });
      }
    }
    if (on.has("transferings"))
      for (const x of source.transferings || []) {
        if (has(existing.transferings, (t) => sid(t.thirdParty), x)) continue;
        const legals = (x.legals || []).map(({ _id, ...legal }) => legal);
        steps.push({ action: "create", store: "datasubjectTransfering/create", payload: { item: { legals, methods: x.methods || [], preventions: x.preventions || [], note: x.note, thirdParty: x.thirdParty, datasubject: { _id: target.rowId } } } });
      }
    if (on.has("datasubjectRights"))
      for (const item of source.datasubjectRights || []) {
        if (has(existing.datasubjectRights, itemKey, item)) continue;
        if (item.libraryItem) steps.push({ action: "create", store: "datasubjectDatasubjectRight/addFromLibrary", payload: { datasubject: { _id: target.rowId }, libraryItem: libRef(item) } });
        else skipped.push({ key: "datasubjectRights", item });
      }
    if (on.has("feature") && source.feature) {
      const cur = existing.feature || {};
      const patch = {};
      if (source.feature.autoProcessing && !cur.autoProcessing) patch.autoProcessing = true;
      if (source.feature.aiProcessing && !cur.aiProcessing) patch.aiProcessing = true;
      if (Object.keys(patch).length) steps.push({ action: "update", store: "organizationProcessDataDatasubject/updateFeature", payload: { _id: target.rowId, ...patch } });
    }
  }
  return { steps, skipped };
};

/** Satırın ilgili kişisi (kütüphane/organizasyon ilgili kişisi; yoksa ad) — iki veri arasında satır eşleştirmesi. */
export const datasubjectKeyOf = (row = {}) => (row.datasubject ? `d:${sid(row.datasubject)}` : `n:${nameKey(row.name)}`);

/**
 * Veri kopyası: kaynak verinin etkin özellikleri hedef veriye; aynı ilgili kişiyi taşıyan satırların satır özellikleri hedefin o
 * satırına. `rowInv`: satır kimliği → satır envanteri. Döner: { steps, skipped, unmatched } — `unmatched` hedefte karşılığı olmayan
 * kaynak satırları (satır özellikleri kopyalanmadı).
 */
/** TKP-214: iki anlık görüntünün torba ve liste alanlarının birleşimi (kopyada "zaten var" denetimi için). */
const mergeSnapshots = (a = {}, b = {}) => {
  const bags = {};
  for (const key of new Set([...Object.keys(a.bags || {}), ...Object.keys(b.bags || {})])) bags[key] = [...((a.bags || {})[key] || []), ...((b.bags || {})[key] || [])];
  const list = (k) => [...(a[k] || []), ...(b[k] || [])];
  return { bags, disposalMethods: list("disposalMethods"), accessAndRestrictions: list("accessAndRestrictions"), administrativeMeasures: list("administrativeMeasures"), technicalMeasures: list("technicalMeasures") };
};

/** TKP-214: hedefte açılacak ilgili kişi satırının yer tutucusu — uygulayıcı satırı açınca sonraki adımlarda gerçek kimlikle değişir. */
export const newRowRef = (targetNode, sourceRow) => `@yeni-satir:${sid(targetNode)}:${sid(sourceRow)}`;

/**
 * `rowIds`: kopyalanacak kaynak ilgili kişi satırları (verilmezse hepsi). TKP-214: hedefte karşılığı olmayan seçili kişi önce hedef
 * veriye EKLENİR (`createRow`), sonra özellikleri kopyalanır — yeni (kişisiz) veriye başka verinin ilgili kişileri özellikleriyle taşınır.
 */
export const nodeCopyPlan = ({ sourceNode, targetNode, nodeInv = {}, rowInv = {}, sections = [], existingMeasures, rowIds }) => {
  const targetSnap = nodeSnapshot(targetNode, nodeInv[sid(targetNode)]);
  if (existingMeasures) Object.assign(targetSnap, existingMeasures); // bekleyen taslak tedbirler kipin tabanı (TKP-200 m.17)
  const sourceSnap = nodeSnapshot(sourceNode, nodeInv[sid(sourceNode)]);
  const targetRows = (targetNode.datasubjects || []).filter((r) => !r.__draft);
  const steps = [];
  const skipped = [];
  // TKP-214 / K7: kaynağın veri seviyesi değerleri hedefte İLGİLİ KİŞİ satırlarına (kişili hedef), yalnız kişisiz hedefte veriye
  if (targetRows.length) {
    for (const row of targetRows) {
      const r = levelSteps({ source: sourceSnap, existing: rowSnapshot(row, rowInv[sid(row)]), inherited: targetSnap, target: { rowId: sid(row), nodeId: sid(targetNode) }, sections });
      steps.push(...r.steps);
      if (row === targetRows[0]) skipped.push(...r.skipped); // kütüphanesiz kalem uyarısı bir kez
    }
  } else {
    const node = levelSteps({ source: sourceSnap, existing: targetSnap, target: { nodeId: sid(targetNode) }, sections });
    steps.push(...node.steps);
    skipped.push(...node.skipped);
  }
  const rowInherited = targetRows.length ? { ...targetSnap, ...mergeSnapshots(targetSnap, sourceSnap) } : targetSnap; // satır kopyasında aynı kalem ikinci kez yazılmaz
  const unmatched = [];
  const wanted = rowIds ? new Set(rowIds.map(sid)) : null;
  const targets = new Map((targetNode.datasubjects || []).filter((r) => !r.__draft).map((r) => [datasubjectKeyOf(r), r]));
  for (const row of (sourceNode.datasubjects || []).filter((r) => !r.__draft)) {
    if (wanted && !wanted.has(sid(row))) continue;
    const hit = targets.get(datasubjectKeyOf(row));
    if (!hit) {
      if (!row.datasubject) { // organizasyon ilgili kişisi çözülemeyen eski satır — eklenemez
        if (sectionTotal(rowSnapshot(row, rowInv[sid(row)]), COPY_SECTIONS.map((x) => x.key)) > 0) unmatched.push(row);
        continue;
      }
      // TKP-214: hedef veriye bu ilgili kişi eklenir, sonra satır özellikleri yeni satıra
      // TKP-227: yalnız seçili bölümlerden yeni satıra yazılacak bir değer varsa (bölüm seçilmeden kişi eklenmez)
      const ref = newRowRef(targetNode, row);
      const r = levelSteps({ source: rowSnapshot(row, rowInv[sid(row)]), existing: {}, inherited: {}, target: { rowId: ref, nodeId: sid(targetNode) }, sections });
      if (!r.steps.length) continue;
      steps.push({ action: "createRow", store: "organizationProcessDataDatasubject/create", payload: { processData: { _id: sid(targetNode) }, datasubject: { _id: sid(row.datasubject) } }, ref });
      steps.push(...r.steps);
      skipped.push(...r.skipped);
      continue;
    }
    const r = levelSteps({ source: rowSnapshot(row, rowInv[sid(row)]), existing: rowSnapshot(hit, rowInv[sid(hit)]), inherited: rowInherited, target: { rowId: sid(hit), nodeId: sid(targetNode) }, sections });
    steps.push(...r.steps);
    skipped.push(...r.skipped);
  }
  return { steps, skipped, unmatched };
};

/** TKP-214: adımdaki yer tutucuları (`@yeni-satir:…`) açılan satırların gerçek kimlikleriyle değiştirir (derin, girdi değişmez). */
export const resolveRefs = (value, refs) => {
  if (typeof value === "string") return Object.prototype.hasOwnProperty.call(refs, value) ? refs[value] : value;
  if (Array.isArray(value)) return value.map((v) => resolveRefs(v, refs));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, resolveRefs(v, refs)]));
  return value;
};

/** İlgili kişi kopyası: kaynak satırın kendi özellikleri hedef satıra (hedef verinin etkin özelliklerinde olan kalem atlanır). */
export const rowCopyPlan = ({ sourceRow, targetRow, targetNode, nodeInv = {}, rowInv = {}, sections = [] }) =>
  levelSteps({
    source: rowSnapshot(sourceRow, rowInv[sid(sourceRow)]),
    existing: rowSnapshot(targetRow, rowInv[sid(targetRow)]),
    inherited: targetNode ? nodeSnapshot(targetNode, nodeInv[sid(targetNode)]) : {},
    target: { rowId: sid(targetRow), nodeId: sid(targetNode) },
    sections,
  });

const sectionTotal = (snap, sections) => (sections || []).reduce((n, key) => n + sectionCount(snap, key), 0);

/** Pencerenin bölüm sayıları için kaynak anlık görüntüleri: veri kopyasında veri + eşleşen satırlar, ilgili kişi kopyasında satır. */
export const sourceSnapshots = ({ kind, sourceNode, sourceRow, nodeInv = {}, rowInv = {}, rowIds }) => {
  if (kind === "row") return sourceRow ? [rowSnapshot(sourceRow, rowInv[sid(sourceRow)])] : [];
  if (!sourceNode) return [];
  // TKP-214: seçilen ilgili kişiler (hedefte olmasa da — kopyada hedefe eklenir); verilmezse hepsi
  const wanted = rowIds ? new Set(rowIds.map(sid)) : null;
  return [nodeSnapshot(sourceNode, nodeInv[sid(sourceNode)]), ...(sourceNode.datasubjects || []).filter((r) => !r.__draft && (!wanted || wanted.has(sid(r)))).map((r) => rowSnapshot(r, rowInv[sid(r)]))];
};
